import React, { useEffect, useState } from 'react';
import { DollarSign, Save, Loader2, Info } from 'lucide-react';
import { api } from '../../services/api';

export function AdminSettingsTab() {
  const [percent, setPercent] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/admin/settings/commission');
        setPercent(String(res.data?.percent ?? 15));
      } catch (e) {
        // fallback default
        setPercent('15');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const save = async () => {
    const num = parseFloat(percent);
    if (isNaN(num) || num < 0 || num > 100) {
      alert('Bitte einen Wert zwischen 0 und 100 eingeben.');
      return;
    }
    setSaving(true);
    setSaved(false);
    try {
      await api.put('/admin/settings/commission', { percent: num });
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      alert(e.response?.data?.detail || 'Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-settings-tab">
      <div className="bg-white rounded-xl border border-slate-100 p-6 max-w-2xl">
        <div className="flex items-start gap-3 mb-5">
          <div className="w-10 h-10 rounded-lg bg-orange-50 flex items-center justify-center shrink-0">
            <DollarSign size={20} className="text-orange-500" />
          </div>
          <div>
            <h2 className="font-bold text-slate-800">Portal-Kommission</h2>
            <p className="text-sm text-slate-500 mt-0.5">
              Prozentsatz, den 366Transfer von jedem gebuchten Transfer einbehält.
            </p>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-10"><Loader2 size={24} className="animate-spin text-slate-300" /></div>
        ) : (
          <>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Kommission (%)
            </label>
            <div className="flex items-center gap-3">
              <div className="relative flex-1 max-w-xs">
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.5"
                  value={percent}
                  onChange={(e) => setPercent(e.target.value)}
                  className="w-full pl-4 pr-12 py-3 border border-slate-200 rounded-lg text-lg font-semibold focus:border-orange-500 focus:ring-1 focus:ring-orange-500 outline-none"
                  data-testid="commission-percent-input"
                />
                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 font-medium">%</span>
              </div>
              <button
                onClick={save}
                disabled={saving}
                className="px-5 py-3 bg-orange-500 hover:bg-orange-600 text-white rounded-lg text-sm font-semibold disabled:opacity-50 flex items-center gap-2"
                data-testid="commission-save-btn"
              >
                {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                Speichern
              </button>
            </div>

            {saved && (
              <p className="mt-3 text-sm text-emerald-600 flex items-center gap-1.5" data-testid="commission-saved-msg">
                ✓ Kommission gespeichert
              </p>
            )}

            <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-100 flex gap-3">
              <Info size={16} className="text-blue-500 shrink-0 mt-0.5" />
              <div className="text-xs text-blue-900 leading-relaxed">
                <p className="font-semibold mb-1">Beispielrechnung bei {percent || 0}%:</p>
                <p>Transfer-Preis: <b>100 €</b></p>
                <p>Portal-Kommission ({percent || 0}%): <b>−{((parseFloat(percent) || 0)).toFixed(2)} €</b></p>
                <p>Netto an Fahrer: <b>{(100 - (parseFloat(percent) || 0)).toFixed(2)} €</b></p>
                <p className="mt-2 text-blue-700">
                  Hinweis: Bei jeder Buchung wird der aktuelle Prozentsatz fest gespeichert.
                  Spätere Änderungen wirken nur auf neue Buchungen.
                </p>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
